import { Button } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { Eye, UserMinus } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useWebSocket from 'react-use-websocket';
import LoadingSpinner from '../../../components/buttons/LoadingSpinner';
import SearchTable from '../../../components/inputs/search/SearchTable';
import Table from '../../../components/tables/Table';
import TableItem from '../../../components/tables/TableItem';
import TableRow from '../../../components/tables/TableRow';
import TicketTag from '../../../components/tags/TicketTag';
import Typography from '../../../components/typography/Typography';
import EmployeeSelect from '../../../components/users/EmployeeSelect';
import { TicketService } from '../../../services/tickets/Tickets';
import { UsersService } from '../../../services/users/Users';
import { setRootAside } from '../../../state/component/AsideSlice';
import { RootState } from '../../../state/store';
import {
  Ticket,
  appendTicket,
  fetchTicketsGeneric,
  updateTicket,
} from '../../../state/tickets/ticketsSlice';
import ListComponent from '../../../utils/ListComponent';
import { TruncateString } from '../../../utils/text/TruncateString';
const TicketContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [employees, setEmployees] = useState();
  const [reAsign, setReAsign] = useState(false);
  const [socketMessage, setSocketMessages] = useState();

  const companyId = useSelector(
    (state: RootState) => state.companies.selectedCompany?.id
  );

  const tickets = useSelector(
    (state: RootState) => state.tickets.departmentTickets
  );
  const { lastMessage } = useWebSocket(
    'wss://o9kqmb8mva.execute-api.eu-west-1.amazonaws.com/v1/'
  );

  const userService = new UsersService();
  const ticketService = new TicketService();

  const tableHeaders = [
    '#',
    'Description',
    'Klant',
    'Tags',
    'Technieker',
    'Aanmaakdatum',
    '',
  ];
  const fixedColumns = [
    { key: ' ', position: 'left' },
    { key: '', position: 'right' },
  ];

  const watchForNewTicket = async (id: number) => {
    if (!id) return;
    const ticket = await ticketService.getTicketBo(id);
    dispatch(appendTicket(ticket.ticket));
  };

  const fetchEmployees = async () => {
    const employeesResult = await userService.getAllEmployees();
    setEmployees(employeesResult.employees);
  };

  useEffect(() => {
    fetchEmployees();
    if (companyId) {
      dispatch(fetchTicketsGeneric({ department: 'IT' }));
    }
  }, [companyId]);

  useEffect(() => {
    if (lastMessage && lastMessage.data) {
      let parsedData;
      try {
        parsedData = JSON.parse(lastMessage.data);
        const ticketId = parsedData.ticket_id;
        watchForNewTicket(ticketId);
      } catch (error) {
        console.error(
          'Error parsing message.data JSON:',
          error,
          'Raw data:',
          lastMessage.data
        );
      }
    }
  }, [lastMessage]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleAssignTicket = (value: string, values: string) => {
    setReAsign(false);
    const ticket_id = values.option;
    const employee = employees.find((item) => item.med_id === value);
    dispatch(
      updateTicket({
        tic_id: ticket_id,
        tic_geb_id: employee.med_id,
        tic_status: 'Toegewezen',
        assignedEmployee: {
          med_naam: employee.med_naam,
          med_foto: employee.med_foto,
        },
      })
    );

    const update = {
      tic_med_id: employee.med_id,
      status: 'Toegewezen',
    };

    const updatedTicket = ticketService.updateTicket(ticket_id, update);
  };

  const filteredTickets = useMemo(() => {
    if (!search.trim()) {
      return tickets;
    }

    const lowerCaseSearch = search.toLowerCase();
    return tickets.filter(
      (ticket) =>
        ticket.tic_onderwerp.toLowerCase().includes(lowerCaseSearch) ||
        ticket.tic_omschrijving.toLowerCase().includes(lowerCaseSearch) ||
        (ticket.assignedEmployee?.med_naam?.toLowerCase() || '').includes(
          lowerCaseSearch
        )
    );
  }, [tickets, search]);

  const viewDetail = (ticket: Ticket) => {
    dispatch(
      setRootAside({
        component: 'BoTicketDetailContainer',
        data: { ticket },
        title: 'Ticket',
      })
    );
  };

  const createTicket = () => {
    dispatch(
      setRootAside({
        component: 'ticketCreate',
        data: {},
        title: 'Ticket aanmaken',
      })
    );
  };

  return (
    <div className="border overflow-y-hidden dark:border-gray-700 rounded-lg">
      <div className="px-6 py-2 border-b dark:border-gray-700 flex justify-between items-center">
        {t('Gevonden')}: {filteredTickets.length}
        <div className="flex gap-3 items-center">
          <Button onClick={createTicket} className="bg-blue-700 text-white">
            {t('+ Ticket')}
          </Button>
        </div>
      </div>
      <SearchTable
        handleChange={handleSearchChange}
        placeholder={t('Tickets zoeken...')}
        search={search}
      />
      <Table header={tableHeaders} fixedColumns={fixedColumns}>
        {filteredTickets?.length > 0 ? (
          <ListComponent
            data={filteredTickets}
            renderItem={(ticket) => (
              <TableRow key={ticket.tic_id} className="cursor-pointer">
                <TableItem type="medium">
                  <span onClick={() => viewDetail(ticket)}>
                    #{ticket.tic_id}
                  </span>
                </TableItem>
                <TableItem type="medium">
                  <TruncateString
                    text={ticket.tic_omschrijving.replace(/[#*]/g, '')}
                    limit={90}
                    popover={false}
                  />
                </TableItem>
                <TableItem type="medium">{ticket.company.bedNaam} </TableItem>
                <TableItem type="medium">
                  <div className="flex gap-1">
                    <TicketTag
                      style={ticket.tic_status}
                      label={ticket.tic_status}
                    />
                    <TicketTag
                      label={ticket.company.bedLevel}
                      style={ticket.company.bedLevel}
                    />
                  </div>
                </TableItem>
                <TableItem type="medium">
                  {ticket.assignedEmployee.med_naam &&
                  reAsign != ticket.tic_id ? (
                    <div className="flex items-center">
                      <Button
                        onClick={() =>
                          setSearch(ticket.assignedEmployee.med_naam)
                        }
                      >
                        <div className="flex items-center">
                          <img
                            src={ticket.assignedEmployee.med_foto}
                            alt={ticket.assignedEmployee.med_foto}
                            className="w-6 h-6 rounded-full object-cover mr-2"
                          />
                          <Typography tag="span" type="small">
                            {ticket.assignedEmployee.med_naam}
                          </Typography>
                        </div>
                      </Button>
                      <UserMinus
                        size={14}
                        onClick={() => setReAsign(ticket.tic_id)}
                        className="ml-2"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center">
                      {employees ? (
                        <EmployeeSelect
                          employees={employees}
                          onChange={handleAssignTicket}
                          option={ticket.tic_id}
                        />
                      ) : (
                        <LoadingSpinner />
                      )}
                    </div>
                  )}
                </TableItem>
                <TableItem type="medium">{ticket.created_at}</TableItem>
                <TableItem type="medium">
                  <button onClick={() => viewDetail(ticket)}>
                    <Eye size={18} />
                  </button>
                </TableItem>
              </TableRow>
            )}
          />
        ) : (
          <tr>
            <td colSpan={tableHeaders.length} className="text-center py-4">
              {t('Geen tickets gevonden.')}
            </td>
          </tr>
        )}
      </Table>
    </div>
  );
};

export default TicketContainer;
